import { Icon, LogOut } from "@norican/icons";
import { Button, cn } from "@norican/react";
import { type ReactNode, useEffect } from "react";
import { NavLink, useNavigate } from "react-router";

import { NoricanLogo } from "@/components/brand/norican-logo";
import { useLogout, useMe } from "@/lib/api";
import { logger } from "@/lib/logger";
import { LOGIN_POPUP_NAME } from "@/lib/login-popup";

interface AppShellProps {
  children: ReactNode;
}

const NAV_ITEMS = [
  { to: "/home", label: "My card" },
  { to: "/colleagues", label: "Colleagues" },
];

/**
 * Page chrome: white header with the secondary Norican logo (wordmark only,
 * the guideline's choice for small formats), the two sections, sign-out, and
 * a Base Grey canvas for the content.
 */
export function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: me } = useMe();

  // When this window was opened as the Teams sign-in popup, the session now
  // exists and the opener (the tab inside Teams) picks it up by polling
  // /auth/me. Nothing is left to show here, so close the popup.
  useEffect(() => {
    if (me && window.name === LOGIN_POPUP_NAME && window.opener) {
      logger.debug("[auth] sign-in popup finished, closing");
      window.close();
    }
  }, [me]);

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-brand-grey-base text-fg flex min-h-screen flex-col">
      <header className="bg-bg border-border border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-4 py-3 sm:gap-4 sm:px-6">
          <NavLink
            to="/home"
            className="text-brand-blue-700 flex shrink-0 items-center"
            aria-label="Norican"
          >
            <NoricanLogo variant="secondary" className="h-5 w-auto sm:h-6" />
          </NavLink>
          <nav className="flex items-center gap-0.5 sm:gap-1" aria-label="Sections">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors sm:px-3",
                    isActive
                      ? "bg-accent-subtle text-accent-fg-subtle"
                      : "text-fg hover:bg-surface-hover",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          {/* On phones the label gives way to the icon so the header never overflows the viewport. */}
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            onClick={handleLogout}
            aria-label="Sign out"
            className="shrink-0"
            startIcon={<Icon icon={LogOut} size={16} />}
          >
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6">{children}</main>
      <footer className="text-fg-muted mx-auto w-full max-w-5xl px-4 py-6 text-xs sm:px-6">
        Norican Group · Metal. Made. Possible.
      </footer>
    </div>
  );
}
